import { zodResolver } from "@hookform/resolvers/zod";
import { Ban } from "@tamagui/lucide-icons";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { XStack, YStack } from "tamagui";
import { z } from "zod";

import {
  useFetchMedicationHistory,
  useGetMedicationHistoryStatus,
} from "@/api/generated";
import {
  FetchRxHistoryWorkflowStatusStatus,
  WorkflowType,
} from "@/api/generated/models";
import { useSendNoriMessage } from "@/api/mutations/conversation";
import {
  Button,
  Footer,
  Heading,
  LoadingSpinner,
  Paragraph,
  RadioGroup,
  ScrollView,
  Text,
} from "@/components";
import { PrescriptionScanFlow } from "./prescription-scan/PrescriptionScanFlow";

const rxRenewalSchema = z.object({
  selectedMedicationId: z
    .string()
    .min(1, "Please select a prescription to renew"),
});

type RxRenewalFormData = z.infer<typeof rxRenewalSchema>;

export const RxRenewals = ({ onClose }: { onClose: () => void }) => {
  const [showPhotoUpload, setShowPhotoUpload] = useState(false);
  const { mutate: fetchMedications } = useFetchMedicationHistory();
  const { sendNoriMessage, isPending } = useSendNoriMessage();

  const { control, handleSubmit } = useForm<RxRenewalFormData>({
    resolver: zodResolver(rxRenewalSchema),
    defaultValues: { selectedMedicationId: "" },
  });

  const { data: rxHistoryStatus, refetch: refetchRxHistory } =
    useGetMedicationHistoryStatus({
      query: {
        refetchInterval: (query) =>
          query.state.data?.status ===
          FetchRxHistoryWorkflowStatusStatus.in_progress
            ? 5000
            : false,
      },
    });

  useEffect(() => {
    fetchMedications();
  }, [fetchMedications]);

  const isLoading =
    !rxHistoryStatus ||
    rxHistoryStatus.status === FetchRxHistoryWorkflowStatusStatus.in_progress;
  const isFailed =
    rxHistoryStatus?.status === FetchRxHistoryWorkflowStatusStatus.failed;
  const medications = rxHistoryStatus?.medications ?? [];
  const nonRenewableMedications =
    rxHistoryStatus?.non_renewable_medications ?? [];
  const noMedications = !isLoading && !isFailed && medications.length === 0;

  const handleRetry = () => {
    fetchMedications();
    refetchRxHistory();
  };

  const handleContinue = handleSubmit(({ selectedMedicationId }) => {
    const selected = medications.find(
      (m) =>
        String(m.patient_vendor_medication_history_id) === selectedMedicationId,
    )!;
    sendNoriMessage([selected], {
      type: "rx_renewal_selected_medication",
      agent: "prescription_renewal_consent_agent",
      workflowType: WorkflowType.rx_renewal,
      onConversationCreation: onClose,
    });
  });

  const handleTalkToDoctor = () => {
    // No medication was found so we fall back to the generalist for intake.
    sendNoriMessage(
      "I'd like to talk to a doctor about renewing a prescription",
      {
        onConversationCreation: onClose,
      },
    );
  };

  if (showPhotoUpload) {
    return <PrescriptionScanFlow onClose={onClose} />;
  }

  if (isLoading) {
    return (
      <YStack
        flex={1}
        justifyContent="center"
        alignItems="center"
        gap="$100"
        paddingHorizontal="$200"
        paddingBottom="$200"
      >
        <LoadingSpinner size="$1000" />
        <Text semibold>Looking for prescriptions</Text>
        <Text color="$textSecondary">This can take up to 3 minutes...</Text>
      </YStack>
    );
  }

  if (isFailed || noMedications) {
    return (
      <YStack gap="$200" flex={1} paddingHorizontal="$200" paddingBottom="$200">
        <YStack gap="$100">
          <Heading size="$xl">We hit a snag...</Heading>
          <Paragraph color="$textSecondary" size="$s">
            We weren't able to pull any prescriptions from your records. You can
            try again later or talk to one of our clinicians to process your
            renewal.
          </Paragraph>
        </YStack>
        <Footer marginTop="auto" mode="dialog">
          <Button
            mode="primary"
            constrained
            onPress={handleRetry}
            dataTrackingId="rx-renewals-retry-button"
          >
            Try again
          </Button>
          <Button
            mode="tertiary"
            onPress={handleTalkToDoctor}
            loading={isPending}
            dataTrackingId="rx-renewals-talk-to-doctor-button"
          >
            Not finding your prescription? Talk to a doctor.
          </Button>
        </Footer>
      </YStack>
    );
  }

  return (
    <YStack gap="$200" flex={1} paddingHorizontal="$200" paddingBottom="$200">
      <YStack gap="$100">
        <Heading size="$xl">Refill request(s)</Heading>
        <Paragraph color="$textSecondary" size="$s">
          We were able to pull the following medication prescriptions from your
          records.
        </Paragraph>
      </YStack>

      <ScrollView flex={1} contentContainerStyle={{ gap: "$200" }}>
        <Controller
          control={control}
          name="selectedMedicationId"
          render={({ field, fieldState }) => (
            <RadioGroup
              dataTrackingId="rx-renewals-med-option"
              label="Which of these prescriptions are you looking to renew?"
              labelProps={{ size: "$md", bold: true }}
              value={field.value}
              onValueChange={field.onChange}
              errorText={fieldState.error?.message}
              disabled={isPending}
              borderless
            >
              {medications.map((med) => (
                <RadioGroup.Item
                  key={med.patient_vendor_medication_history_id}
                  id={`renewal-${med.patient_vendor_medication_history_id}`}
                  value={String(med.patient_vendor_medication_history_id)}
                >
                  {med.prescription_details.display_name}
                </RadioGroup.Item>
              ))}
            </RadioGroup>
          )}
        />
        {nonRenewableMedications.length > 0 && (
          <YStack gap="$150" marginTop="$100">
            <Text semibold>Not eligible for renewal at Curai</Text>
            <YStack gap="$100">
              {nonRenewableMedications.map((med) => (
                <XStack
                  key={med.patient_vendor_medication_history_id}
                  gap="$100"
                  alignItems="center"
                >
                  <Ban size="$100" color="$textSecondary" />
                  <Text color="$textSecondary">
                    {med.prescription_details.display_name ??
                      med.prescription_details.generic_drug_name}
                  </Text>
                </XStack>
              ))}
            </YStack>
          </YStack>
        )}
      </ScrollView>
      <Footer mode="dialog">
        <Button
          mode="primary"
          constrained
          onPress={handleContinue}
          loading={isPending}
          dataTrackingId="rx-renewals-continue-button"
        >
          Continue
        </Button>
        <Button
          mode="tertiary"
          onPress={() => setShowPhotoUpload(true)}
          dataTrackingId="rx-renewals-upload-photo-button"
        >
          Don't see your prescription? Upload a photo
        </Button>
      </Footer>
    </YStack>
  );
};
